import { cx } from '@/utils';
import Button from '@common/Button';
import DotButton, { useDotButton } from '@common/DotButton/DotButton';
import SvgArrow from '@common/Icons/Arrow';
import SvgArrow2 from '@common/Icons/Arrow2';
import SvgAttendance from '@common/Icons/Attendance';
import SvgCalendar from '@common/Icons/Calendar';
import SvgPay2 from '@common/Icons/Pay2';
import SvgTime from '@common/Icons/Time';
import SvgWithoutPay from '@common/Icons/WithoutPay';
import {
  CostTypes,
  DurationTypes,
  ModalityTypes,
  PageProceduresBlock,
  ProcedureTypes,
} from '@common/Providers/GraphqlProvider/generated';
import { t } from '@lingui/macro';
import { COLORS, ROUTES_CLIENT } from '@utils/constants';
import { TitleWrapper } from '@views/Home/components';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';
import useEmblaCarousel from 'embla-carousel-react';

import IconNoResults from '@/components/common/Icons/IconNoResults';

import styles from './styles.module.css';

const Procedures = ({
  currentPageProceduresBlock,
}: {
  currentPageProceduresBlock?: PageProceduresBlock;
}) => {
  const {
    home: { proceduresBlock: proceduresBlockContext },
  } = useHomeContext();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 420px)': { slidesToScroll: 1 },
      '(min-width: 768px)': { slidesToScroll: 3 },
    },
  });

  const proceduresBlock = currentPageProceduresBlock || proceduresBlockContext;

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  const getModalityText = (modality: ModalityTypes) => {
    switch (modality) {
      case ModalityTypes.EnLinea:
        return 'En línea';
      case ModalityTypes.SemiPresencial:
        return 'Semipresencial';
      case ModalityTypes.Presencial:
        return 'Presencial';
    }
  };

  const getCostText = (cost: CostTypes) => {
    switch (cost) {
      case CostTypes.ConCosto:
        return 'Con costo';
      case CostTypes.SinCosto:
        return 'Sin costo';
    }
  };

  if (proceduresBlock?.isDisabled) return null;

  const proceduresSorted = [...(proceduresBlock?.procedures || [])]
    .filter((x) => x.procedureTypes !== ProcedureTypes.Pqrsdf)
    .sort((a, b) => {
      // Si la fecha está vacía, colocar al final
      if (!a.date) return 1;
      if (!b.date) return -1;
      // Ordenar por fecha de forma descendente
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

  const redirectTypeOfTramit = (procedureTypes: ProcedureTypes, procedureid: string) => {
    if (procedureTypes === ProcedureTypes.Tax) {
      window.open(`${ROUTES_CLIENT.TAXES_LIST}/${procedureid}`, '_ blank');
    } else {
      window.open(`${ROUTES_CLIENT.PROCEDURES}/${procedureid}/${procedureTypes}`, '_ blank');
    }
  };

  return (
    <>
      <section className={'mt-10'}>
        <div className={'inner-extend'}>
          <div className={'mb-8'}>
            <TitleWrapper
              title1={!proceduresBlock?.showTitle ? '' : proceduresBlock?.titleProcedures}
              action={{
                text: 'Ver más',
                onClick: () => window.open(ROUTES_CLIENT.PROCEDURES),
              }}
              description={
                !proceduresBlock?.showDescription ? '' : proceduresBlock?.descriptionProcedures
              }
            />
          </div>
          <div className={'relative'}>
            {proceduresSorted.length > 0 ? (
              <>
                <div>
                  <button
                    className={'absolute inset-y-0 -left-5 z-10 hidden rotate-180 md:block'}
                    onClick={() => emblaApi?.scrollPrev()}
                  >
                    <SvgArrow className="text-primary" />
                  </button>

                  <button
                    className={'absolute inset-y-0 -right-5 z-10 hidden md:block'}
                    onClick={() => emblaApi?.scrollNext()}
                  >
                    <SvgArrow className="text-primary" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex h-[100px] w-full animate-fade-in items-center justify-center rounded-b-lg">
                <IconNoResults />
                <p>No hay trámites actualmente</p>
              </div>
            )}
            <div className="overflow-hidden pb-2" ref={emblaRef}>
              <div className={'flex gap-[32px] md:mx-14'}>
                {proceduresSorted?.map((procedure, key) => (
                  <div
                    className={cx('min-w-0 flex-[0_0_100%] md:flex-[0_0_32%]', styles.card)}
                    key={`${procedure.id}-${key}`}
                  >
                    <div className="headline-h6  line-clamp-2 w-full text-black">
                      {procedure.name}
                    </div>
                    <div className={styles.info}>
                      {
                        <div
                          className={cx('', {
                            [styles.statusofservice]:
                              procedure.procedureTypes === ProcedureTypes.Service,
                            [styles.statusofprocedure]:
                              procedure.procedureTypes === ProcedureTypes.Procedure,
                            [styles.statusofprocedureinterestinfo]:
                              procedure.procedureTypes === ProcedureTypes.InformationOfInterest,
                            [styles.statusoftax]: procedure.procedureTypes === ProcedureTypes.Tax,
                          })}
                        >
                          {procedure?.procedureTypes === ProcedureTypes.InformationOfInterest
                            ? 'Información de interés'
                            : procedure?.procedureTypes === ProcedureTypes.Procedure
                              ? 'Trámite'
                              : procedure?.procedureTypes === ProcedureTypes.Service
                                ? 'Servicio'
                                : procedure?.procedureTypes === ProcedureTypes.Tax
                                  ? 'Impuesto'
                                  : null}
                        </div>
                      }
                      <div className={'space-y-[4px]'}>
                        <div className={'flex items-center gap-[8px]'}>
                          <SvgAttendance className="text-primary" />
                          <div className={'body-text1 text-tundora'}>
                            {getModalityText(procedure.modality)}
                          </div>
                        </div>
                        <div className={'flex items-center gap-[8px]'}>
                          {procedure.cost === 'SinCosto' ? (
                            <SvgWithoutPay className="text-primary" />
                          ) : (
                            <SvgPay2 className="ml-[1.5px] mr-[4px] text-primary" />
                          )}
                          <div className={'body-text1  '}>{getCostText(procedure.cost)}</div>
                        </div>
                        <div className={'flex items-center gap-[8px]'}>
                          <SvgTime className="text-primary" />
                          <div className={'body-text1 text-tundora'}>
                            {' '}
                            {procedure?.duration === DurationTypes.BusinessDays
                              ? 'Días laborables'
                              : procedure?.duration === DurationTypes.Immediate
                                ? 'Inmediato'
                                : procedure?.duration === DurationTypes.WorkingHours
                                  ? 'Horas de trabajo'
                                  : null}
                          </div>
                        </div>
                        <div className={'flex items-center gap-[8px]'}>
                          <SvgCalendar className="text-primary" />
                          <div className={'body-text1 text-tundora'}>{procedure.date}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div
                        onClick={() =>
                          redirectTypeOfTramit(
                            procedure.procedureTypes as ProcedureTypes,
                            procedure.id
                          )
                        }
                        className="mt-4 flex flex-nowrap items-center justify-center"
                      >
                        <Button
                          text={'Gestionar Aquí'}
                          backgroundColor={'bg-primary rounded-lg px-4 py-1  flex-nowrap'}
                          textClassname="text-white"
                          className={styles.button}
                          iconRight={<SvgArrow2 color={COLORS.white} />}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={'dots mt-8'}>
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={cx('dot', {
                  [styles.selected]: index === selectedIndex,
                })}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Procedures;

export function ProceduresSkeleton() {
  return (
    <section className="mt-10">
      <div className="inner-extend">
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-48 rounded bg-gray-200" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="h-32 w-full rounded bg-gray-200" />
            <div className="h-32 w-full rounded bg-gray-200" />
          </div>
        </div>
      </div>
    </section>
  );
}
